import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import apiHelpers from '../../utils/apiHelpers';

const CollectionContext = createContext({
  quantities: {},
  addCopy: () => {},
  setQuantity: () => {},
  removeCard: () => {},
  reload: () => {},
});

/**
 * Holds the whole collection as a { cardId: quantity } map, loaded once, so the
 * "Agregar a la colección" button can show the stored quantity anywhere a card
 * renders without one request per card. Mutations update the map immediately and
 * roll back if the request fails.
 */
export const CollectionProvider = ({ children }) => {
  const [quantities, setQuantities] = useState({});

  const reload = useCallback(() => {
    return apiHelpers
      .getCollectionQuantities()
      .then(response => {
        setQuantities(response.data.quantities || {});
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Applies the optimistic value, then reconciles with what the server stored.
  const apply = useCallback((cardId, optimistic, request) => {
    let previous;
    setQuantities(prev => {
      previous = prev[cardId];
      const next = { ...prev };
      if (optimistic > 0) next[cardId] = optimistic;
      else delete next[cardId];
      return next;
    });

    return request()
      .then(response => {
        const stored = response.data.quantity;
        setQuantities(prev => {
          const next = { ...prev };
          if (stored > 0) next[cardId] = stored;
          else delete next[cardId];
          return next;
        });
      })
      .catch(() => {
        setQuantities(prev => {
          const next = { ...prev };
          if (previous > 0) next[cardId] = previous;
          else delete next[cardId];
          return next;
        });
      });
  }, []);

  const addCopy = useCallback(
    cardId =>
      apply(cardId, (quantities[cardId] || 0) + 1, () =>
        apiHelpers.addCardToCollection(cardId, 1),
      ),
    [apply, quantities],
  );

  const setQuantity = useCallback(
    (cardId, quantity) =>
      apply(cardId, quantity, () =>
        apiHelpers.setCollectionQuantity(cardId, quantity),
      ),
    [apply],
  );

  const removeCard = useCallback(
    cardId =>
      apply(cardId, 0, () => apiHelpers.removeCardFromCollection(cardId)),
    [apply],
  );

  return (
    <CollectionContext.Provider
      value={{ quantities, addCopy, setQuantity, removeCard, reload }}>
      {children}
    </CollectionContext.Provider>
  );
};

export const useCollection = () => useContext(CollectionContext);

export default CollectionContext;
